import { useRef, useCallback, useEffect, useMemo } from "react";
import {
  ExportDropdown,
  ExportDropdownProps,
} from "@components/common/export/export-dropdown";
import { ProjectTableSubject } from "@components/common/faro-table/faro-table-types";
import { ProjectArchivingState, SdbProject } from "@custom-types/project-types";
import { DataViewMode } from "@store/ui/ui-slice-types";
import { useDateTime } from "@hooks/use-date-time";
import { useExportAllPaginated } from "@hooks/use-export-all-paginated";
import { downloadProjectsData, ExportFormat } from "@utils/file-data-export";
import { TableType } from "@utils/track-event/track-event-list";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { convertProjectsResponseToSdbProjects } from "@store/projects/projects-slice-utils";
import { projectStateToApiArchivingState } from "@utils/project-utils";
import type { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  ExportHandlerOptions,
  ExportResult,
} from "@components/common/export/export-dropdown-types";
import { selectedGroupSelector } from "@store/groups/groups-selector";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";

interface Props extends Pick<ExportDropdownProps, "buttonType"> {
  /** Company ID */
  companyId: string;

  /** Recognizes on which subject the table is being used */
  subjectType: ProjectTableSubject;

  /** List of selected groups in table */
  selectedProjects: SdbProject[];

  /** The archiving status of the project */
  projectStatus?: ProjectArchivingState;

  /** True if some entities are selected */
  areEntitiesSelected: boolean;

  /** Column names to exclude from the exported file (e.g. when the data source lacks certain fields) */
  columnsToExclude?: string[];

  /** The current view mode, appended to the tracking data type (e.g. "active projects in map view") */
  viewMode?: DataViewMode;
}

/** Dropdown to export projects in the file format selected by the user */
export function BulkExportProjects({
  companyId,
  subjectType,
  selectedProjects,
  projectStatus,
  areEntitiesSelected,
  buttonType = "bulk",
  columnsToExclude,
  viewMode,
}: Props): JSX.Element | null {
  const { formatDate } = useDateTime();

  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);
  const selectedGroup = useAppSelector(selectedGroupSelector);

  // Cache fetched projects to avoid re-fetching on subsequent exports
  const cachedProjectsRef = useRef<SdbProject[] | null>(null);

  // Track the table count when bulk selection is not active
  const previousTableCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (areEntitiesSelected) {
      return;
    }

    const currentTableCount = selectedProjects.length;

    if (previousTableCountRef.current === null) {
      previousTableCountRef.current = currentTableCount;
      return;
    }

    if (currentTableCount !== previousTableCountRef.current) {
      cachedProjectsRef.current = null;
      previousTableCountRef.current = currentTableCount;
    }
  }, [areEntitiesSelected, selectedProjects.length]);

  // Clear cache when component unmounts (page navigation)
  useEffect(() => {
    return () => {
      cachedProjectsRef.current = null;
    };
  }, []);

  // Generic hook with explicit type parameters
  const { exportAll } = useExportAllPaginated<
    FaroCoreApiTypes.GetProjectsPayload,
    FaroCoreApiTypes.GetProjectsResponse["data"],
    SdbProject
  >();

  /** The name of the subject in which the table is shown for */
  const entityName = useMemo(() => {
    if (subjectType === "group" && selectedGroup) {
      return selectedGroup.name;
    }
    if (subjectType === "workspace" && selectedSdbCompany) {
      return selectedSdbCompany.name;
    }
    return "";
  }, [selectedGroup, selectedSdbCompany, subjectType]);

  /**
   * Fetches all projects with pagination and caches the result. At the moment we don't know the total of the projects
   * or the total of archived projects, so we have to fetch them all to be able to export them.
   *
   * Cache behavior:
   * - Cache is cleared when table count changes while not in bulk selection mode
   * - Cache is cleared on page navigation (component unmount)
   */
  const fetchAllProjects = useCallback(
    async (options?: ExportHandlerOptions): Promise<SdbProject[] | null> => {
      const currentStatus = projectStatus ?? ProjectArchivingState.active;

      // Return cached projects if available
      if (cachedProjectsRef.current) {
        return cachedProjectsRef.current;
      }

      // Fetch all projects with pagination
      const result = await exportAll({
        fetchParams: {
          companyId,
          archivingStates: projectStateToApiArchivingState(currentStatus),
          groupId: selectedGroup?.id,
        },
        fetchPage: async (params) => {
          const response = await faroCoreApiClient.projects.getProjects(params);
          return { data: [response.data], next: response.page.next };
        },
        transform: (dataArray) => {
          return dataArray.flatMap((responseData) =>
            convertProjectsResponseToSdbProjects(responseData)
          );
        },
        onProgress: options?.onProgress,
        signal: options?.signal,
      });

      // If cancelled, return null
      if (result === null) {
        return null;
      }

      // Cache the result
      cachedProjectsRef.current = result;
      return result;
    },
    [companyId, exportAll, projectStatus, selectedGroup?.id]
  );

  /**
   * Unified export handler for both selected and all projects.
   * @returns number of exported entities if succeeded, null if cancelled
   */
  const handleExport = useCallback(
    async (
      format: ExportFormat,
      options?: ExportHandlerOptions
    ): Promise<ExportResult> => {
      const fileName = generateFileName(subjectType, entityName, projectStatus);

      // Determine which projects to export
      const projectsToExport = areEntitiesSelected
        ? selectedProjects
        : await fetchAllProjects(options);

      // If cancelled during fetch, return null
      if (projectsToExport === null) {
        return null;
      }

      // Export the projects
      return await downloadProjectsData({
        fileName,
        projects: projectsToExport,
        formatDate,
        format,
        columnsToExclude,
      });
    },
    [
      subjectType,
      entityName,
      projectStatus,
      areEntitiesSelected,
      selectedProjects,
      fetchAllProjects,
      formatDate,
      columnsToExclude,
    ]
  );

  return (
    <ExportDropdown
      trackingProps={{
        dataType: exportedTableType(subjectType, projectStatus, viewMode),
        numberOfEntities: selectedProjects.length,
        areEntitiesSelected,
      }}
      exportHandler={handleExport}
      toastMessage={{
        success: "Successfully exported projects.",
        error: "Cannot export projects. Please try again.",
      }}
      buttonTooltipText="Export projects"
      buttonType={buttonType}
      shouldShowExportAllDialog={!areEntitiesSelected}
    />
  );
}

/** Returns the file name */
function generateFileName(
  subjectType: ProjectTableSubject,
  entityName: string,
  projectStatus?: ProjectArchivingState
): string {
  switch (subjectType) {
    case "workspace":
      return `Company ${entityName} - ${projectStatus} project list`;

    case "group":
      return `Group ${entityName} - project list`;
  }
}

/** Returns the exported table type for tracking */
function exportedTableType(
  subjectType: ProjectTableSubject,
  projectStatus?: ProjectArchivingState,
  viewMode?: DataViewMode
): string {
  let baseType: string;

  switch (subjectType) {
    case "workspace":
      baseType =
        projectStatus === ProjectArchivingState.active
          ? TableType.activeProjects
          : TableType.archiveProjects;
      break;

    case "group":
      baseType = TableType.groupProjects;
      break;
  }

  return viewMode ? `${baseType} in ${viewMode} view` : baseType;
}
